import React, { useEffect, useMemo, useState } from "react";
import { collaborators } from "../data/collaborators";
import { useLanguage } from '../context/LanguageContext';

const goldEyebrow = { fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', color: '#C9A84C', fontWeight: 600 };
const contactLink = {
  border: '1px solid rgba(201,168,76,0.4)',
  color: '#C9A84C',
  padding: '8px 16px',
  fontSize: '12px',
  letterSpacing: '1px',
  transition: 'background-color 0.2s ease, color 0.2s ease'
};

const CollaboratorsModal = ({ open, onClose }) => {
  const { t, language } = useLanguage();
  const [selectedId, setSelectedId] = useState(null);

  const selected = useMemo(() => collaborators.find((c) => c.id === selectedId) ?? null, [selectedId]);

  const getLocalizedText = (value) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) return value[language] || value.es || value;
    return value;
  };
  const getLocalizedArray = (value) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) return value[language] || value.es || [];
    return value || [];
  };

  useEffect(() => {
    if (!open) setSelectedId(null);
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="collaborators-modal">
      <button className="absolute inset-0 border-0 cursor-default" style={{ background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(10px)' }} onClick={onClose} aria-label={t('affiliate.close')} />

      <div className="relative max-w-5xl w-full overflow-hidden max-h-[90vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.18)' }}>
        {/* Header */}
        <div className="flex justify-between gap-3 items-start p-6 sticky top-0 z-10" style={{ background: '#141414', borderBottom: '1px solid rgba(201,168,76,0.18)' }}>
          <div>
            <span className="font-body" style={goldEyebrow}>{t('collaborators.subtitle')}</span>
            <h2 className="font-display mt-2" style={{ fontSize: '28px', fontWeight: 700, color: '#F5F5F0' }}>{t('collaborators.title')}</h2>
          </div>
          <div className="flex gap-3 items-center">
            {selected && (
              <button
                className="font-body uppercase transition-colors duration-200"
                style={contactLink}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
                onClick={() => setSelectedId(null)}
                data-testid="collaborators-back"
              >
                {t('collaborators.back')}
              </button>
            )}
            <button className="bg-transparent border-0 text-2xl cursor-pointer text-[#C2C1B6] hover:text-[#C9A84C] transition-colors" onClick={onClose} aria-label={t('affiliate.close')} data-testid="collaborators-close">✕</button>
          </div>
        </div>

        {!selected ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {collaborators.map((c) => (
              <button
                key={c.id}
                className="flex gap-4 text-left p-4 cursor-pointer transition-all hover:-translate-y-1 group"
                style={{ background: '#1C1C1C', border: '1px solid rgba(201,168,76,0.12)' }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(201,168,76,0.12)')}
                onClick={() => setSelectedId(c.id)}
                data-testid={`collaborator-card-${c.id}`}
              >
                <div className="w-24 h-24 overflow-hidden flex-shrink-0" style={{ background: '#0A0A0A' }}>
                  <img src={c.photos[0]?.src} alt={getLocalizedText(c.photos[0]?.alt) ?? c.name} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ filter: 'brightness(0.85)' }} />
                </div>
                <div className="flex flex-col">
                  <div className="font-display" style={{ fontSize: '17px', fontWeight: 700, color: '#F5F5F0' }}>
                    {c.name} {c.nickname && <span style={{ color: '#C9A84C' }}>"{c.nickname}"</span>}
                  </div>
                  <div className="font-body mt-1" style={{ fontSize: '12px', color: '#C9A84C' }}>{getLocalizedText(c.role)}</div>
                  <div className="font-body mt-2 line-clamp-2" style={{ fontSize: '13px', fontWeight: 300, color: '#C2C1B6' }}>{getLocalizedText(c.short)}</div>
                  <div className="font-body uppercase mt-3" style={{ fontSize: '11px', letterSpacing: '1.5px', color: '#C9A84C', fontWeight: 600 }}>{t('collaborators.viewProfile')}</div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <CollaboratorDetail collaborator={selected} t={t} getLocalizedText={getLocalizedText} getLocalizedArray={getLocalizedArray} />
        )}
      </div>
    </div>
  );
};

const buildContactLinks = (contact) => [
  contact.instagram && { label: contact.instagram, href: `https://instagram.com/${contact.instagram.replace('@', '')}` },
  contact.instagram_band && { label: contact.instagram_band, href: `https://instagram.com/${contact.instagram_band.replace('@', '')}` },
  contact.instagram_project && { label: contact.instagram_project, href: `https://instagram.com/${contact.instagram_project.replace('@', '')}` },
  contact.spotify && { label: 'Spotify', href: contact.spotify },
  contact.youtube && { label: 'YouTube', href: contact.youtube },
  contact.tiktok && { label: 'TikTok', href: contact.tiktok },
  contact.website && { label: 'Website', href: contact.website },
].filter(Boolean);

const CollaboratorDetail = ({ collaborator, t, getLocalizedText, getLocalizedArray }) => {
  const para = { fontSize: '14px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.8, marginBottom: '12px' };
  const sectionTitle = { ...goldEyebrow, marginBottom: '12px', display: 'block' };
  const contactLinks = useMemo(
    () => (collaborator.contact ? buildContactLinks(collaborator.contact) : []),
    [collaborator.contact]
  );

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row gap-6 mb-8">
        <div className="w-full md:w-1/3">
          <div className="aspect-square overflow-hidden" style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.18)' }}>
            <img src={collaborator.photos[0]?.src} alt={getLocalizedText(collaborator.photos[0]?.alt) ?? collaborator.name} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ filter: 'brightness(0.85)' }} />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="font-display" style={{ fontSize: '26px', fontWeight: 700, color: '#F5F5F0' }}>
            {collaborator.name} {collaborator.nickname && <span style={{ color: '#C9A84C' }}>"{collaborator.nickname}"</span>}
          </h3>
          <p className="font-body mt-1" style={{ fontSize: '14px', color: '#C9A84C' }}>{getLocalizedText(collaborator.role)}</p>
          <p className="font-body mt-3" style={{ fontSize: '14px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.8 }}>{getLocalizedText(collaborator.short)}</p>
        </div>
      </div>

      <div className="mb-8">
        <span className="font-body" style={sectionTitle}>{t('collaborators.bio')}</span>
        {getLocalizedArray(collaborator.bio).map((text, i) => (<p key={`bio-${i}-${text.slice(0, 15)}`} className="font-body" style={para}>{text}</p>))}
      </div>

      {collaborator.project && (
        <div className="mb-8">
          <span className="font-body" style={sectionTitle}>{collaborator.project.title}</span>
          {getLocalizedArray(collaborator.project.description).map((text, i) => (<p key={`proj-${i}-${text.slice(0, 15)}`} className="font-body" style={para}>{text}</p>))}
        </div>
      )}

      {collaborator.collabVideo && (
        <div className="mb-8">
          <a href={collaborator.collabVideo} target="_blank" rel="noopener noreferrer" className="block p-4 transition-all group" style={{ background: '#1C1C1C', border: '1px solid rgba(201,168,76,0.18)' }}>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center flex-shrink-0" style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.18)' }}>
                <svg className="w-7 h-7" style={{ color: '#C9A84C' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-body" style={{ fontSize: '14px', fontWeight: 600, color: '#F5F5F0' }}>{t('collaborators.watchVideo')}</p>
                <p className="font-body" style={{ fontSize: '13px', fontWeight: 300, color: '#C2C1B6' }}>{t('collaborators.documentation')}</p>
              </div>
            </div>
          </a>
        </div>
      )}

      {collaborator.photos.length > 1 && (
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {collaborator.photos.slice(1).map((photo, i) => (
              <div key={`photo-${photo.src.slice(-15)}-${i}`} className="aspect-video overflow-hidden" style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.12)' }}>
                <img src={photo.src} alt={getLocalizedText(photo.alt)} loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ filter: 'brightness(0.85)' }} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <span className="font-body" style={sectionTitle}>{t('collaborators.collaboration')}</span>
        {getLocalizedArray(collaborator.collab).map((text, i) => (<p key={`collab-${i}-${text.slice(0, 15)}`} className="font-body" style={para}>{text}</p>))}
      </div>

      {collaborator.contact && (
        <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(201,168,76,0.18)' }}>
          <span className="font-body" style={sectionTitle}>{t('collaborators.contact')}</span>
          <div className="flex flex-wrap gap-3">
            {contactLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body transition-colors duration-200"
                style={contactLink}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaboratorsModal;
